import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, TrendingUp, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaseStudyCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 animate-pulse">
    <div className="h-56 bg-slate-200" />
    <div className="p-6">
      <div className="h-4 bg-slate-200 rounded w-1/4 mb-4" />
      <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
      <div className="space-y-2">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
      </div>
    </div>
  </div>
);

const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
      } catch (error) {
        console.error('Failed to fetch case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  return (
    <div data-testid="case-studies-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block text-primary font-medium mb-4">Our Work</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              Case Studies
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              See how we've helped businesses transform and grow through our digital solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <CaseStudyCardSkeleton key={i} />)
            ) : (
              caseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/case-studies/${study.slug}`} data-testid={`case-study-card-${study.slug}`}>
                    <article className="group h-full bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl hover:border-primary/20 transition-all duration-300">
                      {study.image_url && (
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={study.image_url}
                            alt={study.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-4 left-4 right-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/90 text-text-primary text-sm font-medium">
                              <Building2 className="h-4 w-4 mr-1 text-primary" />
                              {study.industry}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-6">
                        <p className="text-sm text-text-muted mb-2">{study.client_name}</p>
                        
                        <h2 className="font-heading font-bold text-xl text-text-primary mb-3 group-hover:text-primary transition-colors">
                          {study.title}
                        </h2>
                        
                        <p className="text-text-secondary mb-6 line-clamp-2">
                          {study.problem}
                        </p>
                        
                        {/* Key Results */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          {study.results.slice(0, 2).map((result, i) => (
                            <div key={i} className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center text-primary mb-1">
                                <TrendingUp className="h-4 w-4 mr-1" />
                                <span className="text-xs font-medium">{result.improvement}</span>
                              </div>
                              <p className="text-xs text-text-muted">{result.metric}</p>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {study.technologies.slice(0, 4).map((tech, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 rounded bg-slate-100 text-text-secondary text-xs"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center text-primary font-medium">
                          <span>Read full case study</span>
                          <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-text-secondary mb-8 max-w-xl mx-auto">
              Let's discuss how we can help transform your business with our proven solutions.
            </p>
            <Link to="/contact">
              <button
                data-testid="case-studies-cta-btn"
                className="bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-4 font-medium inline-flex items-center transition-colors"
              >
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
